import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  CaretDownIcon,
  CheckIcon,
  GearSixIcon,
  HexagonIcon,
  MagnifyingGlassIcon,
  PlusCircleIcon,
  UserPlusIcon,
} from '@phosphor-icons/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/contexts/auth-context';
import { teamPath, useSpace } from '@/contexts/space-context';
import type { Team } from '@/api-models/teams';
import { CreateTeamModal } from '@/features/teams/components/CreateTeamModal';
import { InviteMembersModal } from '@/features/teams/components/InviteMembersModal';
import { tierLabel } from '@/api-models/balance';
import { TEAM_ROLE_LABEL, initials, tileColor } from '@/features/teams/components/teamVisuals';

const BRAND_NAME = 'zooid';

/** Tenant switcher at the top of the sidebar — brand tile + active workspace, expanding to a
 *  searchable popover of teams. Shared between the app shell and the settings sidebar. */
export function WorkspaceSwitcher() {
  const { me } = useAuth();
  const { activeTeam, setActiveTeam, teamId, teams } = useSpace();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  // Radix auto-focuses the first menu item on open; steal it back for the search field.
  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => searchRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [open]);

  const personal = teams.find((t) => t.is_personal);
  const orgTeams = teams.filter((t) => !t.is_personal);

  const q = query.trim().toLowerCase();
  const filteredTeams = q ? orgTeams.filter((t) => t.name.toLowerCase().includes(q)) : orgTeams;
  const personalMatches =
    !q ||
    (personal?.name.toLowerCase().includes(q) ?? false) ||
    me.name.toLowerCase().includes(q) ||
    'personal'.includes(q);

  const activeLabel = activeTeam?.is_personal ? 'Personal' : (activeTeam?.name ?? me.name);
  const triggerSubtitle = activeTeam ? `${activeLabel} · ${tierLabel(activeTeam.license_tier)}` : me.name;

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) setQuery('');
  }

  function selectTeam(team: Team) {
    setActiveTeam(team);
    setOpen(false);
  }

  function go(path: string) {
    setOpen(false);
    navigate(path);
  }

  return (
    <>
      <DropdownMenu.Root open={open} onOpenChange={handleOpenChange}>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            className="group flex h-10 w-full items-center gap-2.25 rounded-md border border-transparent px-1.5 text-left transition-colors hover:bg-surface-3 data-[state=open]:border-brand-border data-[state=open]:bg-surface-3 focus-visible:outline-none"
          >
            <div className="flex size-7 shrink-0 items-center justify-center rounded-tile bg-brand">
              <HexagonIcon size={16} weight="bold" className="text-white" />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-px">
              <span className="truncate font-mono text-sm font-semibold leading-tight text-fg">{BRAND_NAME}</span>
              <span className="truncate text-2xs font-medium leading-tight text-fg-tertiary">{triggerSubtitle}</span>
            </div>
            <CaretDownIcon
              size={15}
              className="shrink-0 text-fg-tertiary transition-transform group-data-[state=open]:rotate-180"
            />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="start"
            sideOffset={6}
            className="z-dropdown flex w-77 flex-col gap-1 overflow-hidden rounded-lg border border-line-strong bg-surface-2 p-1.5 shadow-(--shadow-overlay) animate-fade-in"
          >
            {/* Search — a plain input; swallow keystrokes so Radix typeahead/nav doesn't hijack them. */}
            <div className="flex h-8.5 items-center gap-2 rounded-tile border border-line-subtle bg-sunken px-2.5">
              <MagnifyingGlassIcon size={15} className="shrink-0 text-fg-tertiary" />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Escape') e.stopPropagation();
                }}
                placeholder="Find a team…"
                className="min-w-0 flex-1 bg-transparent text-xs text-fg outline-none placeholder:text-fg-tertiary"
              />
            </div>

            {filteredTeams.length > 0 && (
              <>
                <div className="flex h-6 items-center px-2">
                  <span className="text-2xs font-semibold uppercase tracking-[0.06em] text-fg-tertiary">
                    Your teams
                  </span>
                </div>
                {filteredTeams.map((team) => (
                  <WorkspaceRow
                    key={team.id}
                    active={activeTeam?.id === team.id}
                    onSelect={() => selectTeam(team)}
                    avatar={
                      <div
                        className="flex size-7.5 shrink-0 items-center justify-center rounded-tile"
                        style={{ backgroundColor: tileColor(team.id) }}
                      >
                        <span className="font-mono text-xs font-semibold leading-none text-white">
                          {team.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    }
                    title={team.name}
                    subtitle={[tierLabel(team.license_tier), TEAM_ROLE_LABEL[team.role]].filter(Boolean).join(' · ')}
                  />
                ))}
              </>
            )}

            {personal && personalMatches && (
              <>
                {filteredTeams.length > 0 && <Divider />}
                <WorkspaceRow
                  active={activeTeam?.id === personal.id}
                  onSelect={() => selectTeam(personal)}
                  avatar={
                    <div className="flex size-7.5 shrink-0 items-center justify-center rounded-full bg-brand">
                      <span className="text-2xs font-semibold leading-none text-white">{initials(me.name)}</span>
                    </div>
                  }
                  title={me.name}
                  subtitle={`Personal · ${tierLabel(personal.license_tier)}`}
                />
              </>
            )}

            <Divider />

            <ActionRow
              icon={<PlusCircleIcon size={16} />}
              label="Create or join a team"
              onSelect={() => setShowCreateTeamModal(true)}
            />
            {/* Members and team settings only make sense for a real team. */}
            {activeTeam && !activeTeam.is_personal ? (
              <>
                <ActionRow
                  icon={<UserPlusIcon size={16} />}
                  label="Invite members"
                  onSelect={() => setShowInviteModal(true)}
                />
                <ActionRow
                  icon={<GearSixIcon size={16} />}
                  label="Team settings"
                  onSelect={() => go(teamPath(activeTeam.id, '/settings'))}
                />
              </>
            ) : (
              <ActionRow
                icon={<GearSixIcon size={16} />}
                label="Settings"
                onSelect={() => go(teamPath(teamId, '/settings'))}
              />
            )}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      {showCreateTeamModal && <CreateTeamModal isOpen onClose={() => setShowCreateTeamModal(false)} />}
      {activeTeam && showInviteModal && (
        <InviteMembersModal isOpen onClose={() => setShowInviteModal(false)} team={activeTeam} />
      )}
    </>
  );
}

function Divider() {
  return <div className="h-px bg-line-subtle" />;
}

function WorkspaceRow({
  avatar,
  title,
  subtitle,
  active,
  onSelect,
}: {
  avatar: ReactNode;
  title: string;
  subtitle: string;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <DropdownMenu.Item
      onSelect={onSelect}
      className={`flex h-11.5 cursor-pointer items-center gap-2.5 rounded-tile px-2 outline-none transition-colors ${
        active ? 'bg-brand-tint' : 'data-highlighted:bg-black/4 dark:data-highlighted:bg-white/6'
      }`}
    >
      {avatar}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-xs font-semibold leading-tight text-fg">{title}</span>
        <span className="truncate text-2xs font-medium leading-tight text-fg-tertiary">{subtitle}</span>
      </div>
      {active && <CheckIcon size={17} weight="bold" className="shrink-0 text-brand-text" />}
    </DropdownMenu.Item>
  );
}

function ActionRow({ icon, label, onSelect }: { icon: ReactNode; label: string; onSelect: () => void }) {
  return (
    <DropdownMenu.Item
      onSelect={onSelect}
      className="flex h-8.5 cursor-pointer items-center gap-2.5 rounded-lg px-2 text-xs font-medium text-fg-secondary outline-none transition-colors data-highlighted:bg-black/4 data-highlighted:text-fg dark:data-highlighted:bg-white/6"
    >
      <span className="shrink-0 text-fg-secondary">{icon}</span>
      {label}
    </DropdownMenu.Item>
  );
}
